// byClaude-start 2026-10-14 13:10:25
/**
 * 确认个人资料页破产资料缓存 slice。
 * 客户从确认页进入其他资料编辑页再返回时，破产资料按离开前内容回显，不恢复默认状态。
 * 缓存范围：破产记录声明勾选状态、有关破产令是否已经解除选项、本次已上传的破产解除令文件。
 * 破产资料是用户属性，缓存按用户隔离（不区分产品）；生命周期仅限流程活跃期间，
 * 退出流程、提交成功时清空；store 经 redux-persist 持久化到 sessionStorage，
 * 刷新页面保留回显、关闭 WebView/标签页随会话自动销毁。
 * @author byClaude
 */
import VhkStore from '@/vhkstore';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** 破产解除令文件条目（与确认页 otherList 条目结构一致） */
export type BankruptcyFileItem = {
  type: string;
  name: string;
  path: string;
  index: number;
  uploadPath: string;
  previewUrl?: string;
};

/** 破产资料缓存（slice state 结构） */
export type BankruptcyCache = {
  /** 写入缓存时的用户id，用于同会话切换账号时丢弃旧缓存 */
  userId?: number | string;
  /** 破产记录声明勾选状态 Y/N */
  isBankrupt: string;
  /** 有关破产令是否已经解除 Y/N */
  bankruptcyReleased: string;
  /** 本次已上传的破产解除令文件 */
  otherList: BankruptcyFileItem[];
};

/** 空串表示无缓存（未写入），与"勾选否"（'N'）区分 */
const initialState: BankruptcyCache = {
  isBankrupt: '',
  bankruptcyReleased: '',
  otherList: [],
};

const bankruptcyCacheSlice = createSlice({
  name: 'bankruptcyCache',
  initialState,
  reducers: {
    /** 整体写入缓存（勾选联动、文件清空后的结果同样同步写入） */
    setBankruptcyCache: (state, action: PayloadAction<BankruptcyCache>) => {
      state.userId = action.payload.userId;
      state.isBankrupt = action.payload.isBankrupt;
      state.bankruptcyReleased = action.payload.bankruptcyReleased;
      state.otherList = action.payload.otherList;
    },
    /** 清空缓存（退出流程、确认资料提交成功后调用，此后以服务端回填为准） */
    clearBankruptcyCache: (state) => {
      state.userId = undefined;
      state.isBankrupt = '';
      state.bankruptcyReleased = '';
      state.otherList = [];
    },
  },
});

export const { setBankruptcyCache, clearBankruptcyCache } = bankruptcyCacheSlice.actions;
export default bankruptcyCacheSlice.reducer;

/**
 * 读取缓存中的破产资料，无缓存、用户不匹配或数据非法（持久化损坏）时返回 null
 * （回退默认值和服务端回填逻辑）。组件挂载时读取一次快照用于回显。
 * @param userId 当前用户id
 * @author byClaude
 */
export function readBankruptcyCache(userId?: number | string): BankruptcyCache | null {
  const cache = VhkStore.getState().bankruptcyCacheReducer as BankruptcyCache;
  // 用户校验：同会话切换账号后旧缓存直接失效
  if (userId === undefined || String(cache.userId) !== String(userId)) return null;
  // 值域校验：初始态（未写入）或持久化数据损坏时不使用
  if (cache.isBankrupt !== 'Y' && cache.isBankrupt !== 'N') return null;
  return {
    isBankrupt: cache.isBankrupt,
    bankruptcyReleased:
      cache.bankruptcyReleased === 'Y' || cache.bankruptcyReleased === 'N'
        ? cache.bankruptcyReleased
        : '',
    otherList: Array.isArray(cache.otherList)
      ? cache.otherList
          .filter((item) => !!item?.path)
          .map((item, index) => ({
            type: item.type || '06',
            name: item.name || '',
            path: item.path,
            index,
            uploadPath: item.uploadPath || '01',
            previewUrl: item.previewUrl,
          }))
      : [],
  };
}
// byClaude-end 2026-10-14 13:10:25

// Contains claude-generated edits.
